import { format } from "date-fns";
import { InternalMessagesForm } from "features/CenterContent/RoleContent/InternalMessage/schema/formSchema";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import { RenderErrorInPath } from "shared/components/forms/RenderErrorInPath";
import {
  Box,
  DropsButton,
  Flex,
  Text,
  Textarea,
  VStack,
} from "shared/components/Spor";
import styled, { useTheme } from "styled-components";

const MessagesListContainer = styled(Flex)`
  /* Hide scrollbar */
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`;

const EditingLockedBox = styled(Box)`
  &:hover {
    #editingLockedArea {
      transition: all 0.25s;
      opacity: 1;
    }
  }
`;

export const MessageList = ({ maxHeight }: { maxHeight?: string }) => {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<InternalMessagesForm>();

  const theme = useTheme();

  const watchedFormField = watch("internalMessages");
  const messages = watchedFormField || [];

  const prevMessages = messages.slice(1);
  const messageTextField = `internalMessages.0.message`;
  const description = watch(messageTextField);

  // contexts are populated by `useFormInternalMessage`
  const contextField = `internalMessages.0.contexts`;
  const contexts = watch(contextField);

  const prevUpdatedAtField = `internalMessages.1.updatedAt`;
  const prevUpdatedAt = watch(prevUpdatedAtField);

  const [editingLocked, setEditingLocked] = useState(prevMessages.length > 0);

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.scrollIntoView();
    }
  }, [ref.current]);

  if (description === undefined) {
    log(
      LogLevel.error,
      "MessageList",
      `FormField (${messageTextField}) does not have a defaultValue. This component is most likely set up wrong.\n
      Use internalMessagesFormDefault() or internalMessagesResponseToForm() as starting value.`,
    );
    return null;
  }

  if (!contexts) {
    return <SkeletonLoader skeletonType="list" />;
  }

  const textField = (
    <VStack gap="12px">
      <EditingLockedBox position="relative" w="100%">
        <Box
          padding="3px" /* Necessary to prevent textarea from clipping during focus (as outline eats 3px) */
        >
          <Textarea
            id="textAreaInternal"
            {...register(messageTextField)}
            tabIndex={editingLocked ? -1 : undefined}
            label={
              prevMessages.length === 0
                ? "Skriv ny melding..."
                : "Nåværende melding"
            }
            resize="none"
          />
          {editingLocked && (
            <Flex
              id="editingLockedArea"
              position="absolute"
              inset="0"
              borderRadius="12px"
              backgroundColor={theme.surfaceSelected}
              opacity="0"
            >
              <DropsButton
                margin="auto"
                variant="floating"
                size="sm"
                onClick={() => {
                  setEditingLocked(false);
                  document.getElementById("textAreaInternal")?.focus();
                }}
              >
                <Text fontWeight="400">Rediger melding</Text>
              </DropsButton>
            </Flex>
          )}
        </Box>
      </EditingLockedBox>
      <RenderErrorInPath
        mt="-12px"
        errors={errors}
        errorPath="internalMessages.0.message"
      />
      <Text variant="xs" width="100%" mb="6px">
        <span style={{ fontWeight: "bold" }}>OBS!</span> Interne meldinger kan
        leses av alle Vy-ansatte. Vær varsom med sensitivt og personlig innhold.
      </Text>
    </VStack>
  );

  if (prevMessages.length === 0) {
    return textField;
  }

  return (
    <MessagesListContainer
      w="100%"
      flexDir="column"
      maxHeight={maxHeight}
      gap="0px"
    >
      {prevUpdatedAt && (
        <Text variant="xs" color={theme.colorTextSecondary}>
          Sist oppdatert {format(prevUpdatedAt, "dd.MM.yyyy 'kl.' HH:mm")}
        </Text>
      )}
      <Box ref={ref} m="6px" my="18px">
        {textField}
      </Box>
    </MessagesListContainer>
  );
};
